/**
 * A página do associado (spec 032, §7, resposta 2: vista por TODA a equipe,
 * nunca pelo portal): nome, telefones, endereço, situação na origem e
 * "atualizado há X"; os veículos dele que estão no ERP; os OUTROS veículos dele
 * na origem pelo botão, ao vivo, pela busca do §3.4 (pelo nome, com o aviso de
 * homônimo); e os tickets desses veículos.
 */
import { useEffect, useRef, useState } from 'react';
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import type { SgrSearchResponse } from '@erp/shared';
import { Button } from '@/components/ui/button';
import { EmptyState, Skeleton, Spinner } from '@/components/ui/misc';
import { cn, formatDateTime } from '@/lib/utils';
import { useTenantProfile } from '@/shared/tenant-profile';
import { DeskSection } from '@/features/tickets/TicketSearchPage';
import { errorMessage } from '@/features/tickets/ticket-format';
import { endCustomerDetailQuery, sgrPick, sgrSearch, useSgr, useSgrPageRefresh, vehicleTicketsQuery } from './sgr-queries';
import { capitalize, pageRefreshText, unavailableText, updatedAgoText } from './sgr-view';
import { Field } from './VehiclePage';

const routeApi = getRouteApi('/_authenticated/end-customers/$endCustomerId');

export function EndCustomerPage() {
  const { endCustomerId } = routeApi.useParams();
  const id = Number(endCustomerId);
  const terms = useTenantProfile().terms;
  const sgr = useSgr();
  const navigate = useNavigate();
  const detailQ = useQuery(endCustomerDetailQuery(id));
  const ticketsQ = useQuery(vehicleTicketsQuery('by-end-customer', id));
  const refresh = useSgrPageRefresh('end-customers', id);
  const [notice, setNotice] = useState<string | null>(null);
  const [others, setOthers] = useState<SgrSearchResponse | null>(null);
  const [othersBusy, setOthersBusy] = useState(false);
  const [opening, setOpening] = useState<string | null>(null);
  const autoRefreshed = useRef(false);
  const c = detailQ.data;

  useEffect(() => {
    if (!c?.stale || autoRefreshed.current || !sgr.available) return;
    autoRefreshed.current = true;
    refresh.mutate(undefined, { onSuccess: (res) => (res.status === 'ok' ? null : setNotice(pageRefreshText(res, sgr.sourceName))) });
  }, [c?.stale, sgr.available]); // eslint-disable-line react-hooks/exhaustive-deps

  if (detailQ.isPending) {
    return (
      <div className="space-y-3" aria-busy>
        <Skeleton className="h-6 w-60" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }
  if (detailQ.isError || !c) return <EmptyState className="h-40" message={`Erro ao carregar: ${errorMessage(detailQ.error)}`} />;

  const address = [
    [c.street, c.number].filter(Boolean).join(', '),
    c.complement,
    c.neighborhood,
    [c.city, c.state].filter(Boolean).join(' / '),
    c.zip,
  ]
    .filter((p) => p && p.trim() !== '')
    .join(' · ');
  const inErp = new Set(c.vehicles.map((v) => v.plate));

  const loadOthers = async () => {
    setOthersBusy(true);
    setOthers(await sgrSearch({ q: c.name, owner: true }, AbortSignal.timeout(30_000)));
    setOthersBusy(false);
  };

  const openVehicle = async (sgrId: string, equipmentId: number | null) => {
    if (equipmentId !== null) {
      await navigate({ to: '/resources/$resourceId', params: { resourceId: String(equipmentId) } });
      return;
    }
    setOpening(sgrId);
    const pick = await sgrPick(sgrId);
    setOpening(null);
    if (pick.status === 'ok') await navigate({ to: '/resources/$resourceId', params: { resourceId: String(pick.equipment.id) } });
    else setNotice(pick.status === 'not_found' ? `Não encontrado no ${sgr.sourceName}.` : unavailableText(sgr.sourceName, pick.reason));
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[13px] text-muted-foreground">{capitalize(terms.contact)}</p>
          <h1 className="text-xl font-semibold">{c.name}</h1>
          <p className="text-xs text-muted-foreground">{`${updatedAgoText(c.syncedAt)} do ${sgr.sourceName || 'cadastro de origem'}`}</p>
        </div>
        {sgr.available ? (
          <Button
            size="sm"
            variant="outline"
            disabled={refresh.isPending}
            onClick={() => refresh.mutate(undefined, { onSuccess: (res) => setNotice(pageRefreshText(res, sgr.sourceName)) })}
          >
            {refresh.isPending ? <Spinner className="size-4" /> : <RefreshCw className="size-3.5" />}
            {`Atualizar do ${sgr.sourceName}`}
          </Button>
        ) : null}
      </div>
      {notice ? (
        <p role="status" className="text-sm text-muted-foreground">
          {notice}
        </p>
      ) : null}
      {c.missingSince ? (
        <p role="alert" className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm dark:border-amber-700 dark:bg-amber-950/40">
          <AlertTriangle className="size-4 text-amber-600" aria-hidden />
          {`Não encontrado no ${sgr.sourceName || 'cadastro de origem'} desde ${formatDateTime(c.missingSince)} — nada foi apagado aqui.`}
        </p>
      ) : null}

      <section className="rounded-lg border bg-card p-4 shadow-xs">
        <div className="grid gap-x-6 gap-y-4 sm:grid-cols-3">
          <Field label="Telefone">{c.phone}</Field>
          <Field label="Telefone adicional">{c.phoneAlt}</Field>
          <Field label={`Situação no ${sgr.sourceName || 'cadastro de origem'}`}>{c.status}</Field>
          <div className="sm:col-span-3">
            <Field label="Endereço">{address || null}</Field>
          </div>
        </div>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-semibold">{`${capitalize(terms.resources)} no ERP`}</h2>
        {c.vehicles.length === 0 ? (
          <EmptyState className="h-16" message="Nenhum ainda." />
        ) : (
          <ul className="divide-y rounded-md border">
            {c.vehicles.map((v) => (
              <li key={v.id} className="flex flex-wrap items-center gap-3 px-3 py-2 text-sm">
                <Link to="/resources/$resourceId" params={{ resourceId: String(v.id) }} className="font-semibold text-primary tabular-nums hover:underline">
                  {v.plate}
                </Link>
                <span className="text-muted-foreground">{[v.brand, v.model].filter(Boolean).join(' ')}</span>
                {v.status ? <span className={cn('text-xs', v.inactive ? 'text-muted-foreground' : 'text-emerald-700 dark:text-emerald-300')}>{v.status}</span> : null}
                <span className="ml-auto text-xs text-muted-foreground">{v.client?.name ?? `sem ${terms.client}`}</span>
              </li>
            ))}
          </ul>
        )}
        {sgr.available ? (
          <div className="space-y-2">
            <Button size="sm" variant="outline" disabled={othersBusy} onClick={() => void loadOthers()}>
              {othersBusy ? <Spinner className="size-4" /> : null}
              {`Outros ${terms.resources} no ${sgr.sourceName}`}
            </Button>
            {others ? <OtherVehicles result={others} inErp={inErp} sourceName={sgr.sourceName} opening={opening} onOpen={openVehicle} terms={terms} /> : null}
          </div>
        ) : null}
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-semibold">{`${capitalize(terms.tickets)} dos ${terms.resources}`}</h2>
        {ticketsQ.isPending ? (
          <Skeleton className="h-24 w-full" />
        ) : ticketsQ.isError ? (
          <EmptyState className="h-24" message={`Erro ao carregar: ${errorMessage(ticketsQ.error)}`} />
        ) : ticketsQ.data.groups.length === 0 ? (
          <EmptyState className="h-24" message={`Nenhum ${terms.ticket}.`} />
        ) : (
          <div className="flex flex-col">
            {ticketsQ.data.groups.map((g) => (
              <DeskSection key={g.desk.id} group={g} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function OtherVehicles({
  result,
  inErp,
  sourceName,
  opening,
  onOpen,
  terms,
}: {
  result: SgrSearchResponse;
  inErp: Set<string>;
  sourceName: string;
  opening: string | null;
  onOpen: (sgrId: string, equipmentId: number | null) => Promise<void>;
  terms: { resource: string; resources: string };
}) {
  if (result.status !== 'ok' || result.kind !== 'vehicles') {
    return <p className="text-sm text-amber-700 dark:text-amber-300">{unavailableText(sourceName, result.status === 'unavailable' ? result.reason : undefined)}</p>;
  }
  const rest = result.items.filter((i) => !inErp.has(i.plate.replace(/[^A-Za-z0-9]/g, '').toUpperCase()));
  return (
    <div className="space-y-1">
      {result.homonyms ? (
        <p className="text-xs text-amber-700 dark:text-amber-300">
          {`Mais de uma pessoa com esse nome no ${sourceName}: os ${terms.resources} abaixo podem ser de outra pessoa.`}
        </p>
      ) : null}
      {rest.length === 0 ? (
        <p className="text-sm text-muted-foreground">{`Nenhum outro no ${sourceName}.`}</p>
      ) : (
        <ul className="divide-y rounded-md border">
          {rest.map((i) => (
            <li key={i.sgrId} className="flex items-center gap-3 px-3 py-2 text-sm">
              <span className="font-semibold tabular-nums">{i.plate}</span>
              <span className="text-muted-foreground">{i.model}</span>
              {i.status ? <span className={cn('text-xs', i.inactive ? 'text-muted-foreground' : 'text-emerald-700 dark:text-emerald-300')}>{i.status}</span> : null}
              <Button size="sm" variant="ghost" className="ml-auto" disabled={opening !== null} onClick={() => void onOpen(i.sgrId, i.equipmentId)}>
                {opening === i.sgrId ? <Spinner className="size-4" /> : null}
                {`Abrir ${terms.resource}`}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
